import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import StartSubpageLayout from '../components/start/StartSubpageLayout';
import SuccessStoriesCarousel from '../components/start/SuccessStoriesCarousel';
import { startCard, startCardPadding } from '../components/start/startPageTokens';
import LogoLoop from '../components/LogoLoop';

/**
 * Module scope so other surfaces reuse the same partners and testimonials —
 * the welcome popup panels import these.
 */
export const SUCCESS_PARTNER_LOGOS = [
  { src: '/eastman.png', alt: 'Eastman' },
  { src: '/h2h.png', alt: 'H2H' },
  { src: '/cann.png', alt: 'Cann' },
  { src: '/hhi.png', alt: 'HHI' },
  { src: '/idb.png', alt: 'IDB' },
  { src: '/art.png', alt: 'Art' },
  { src: '/gh.png', alt: 'GH' },
  { src: '/slate.png', alt: 'Slate' },
  { src: '/coho.png', alt: 'Coho' },
  { src: '/hut.png', alt: 'Hut' },
  { src: '/hum.png', alt: 'Hum' },
  { src: '/heart.png', alt: 'Heart' },
  { src: '/mur.png', alt: 'Mur' },
  { src: '/spring.png', alt: 'Spring' },
];

export const SUCCESS_PARTNER_GALLERY = [
  { src: '/cannpic.png', alt: 'Cann Construction' },
  { src: '/hhipic.png', alt: 'HHI Builders' },
  { src: '/idbpic.png', alt: 'IDB' },
  { src: '/eastpic.png', alt: 'Eastman' },
  { src: '/artpic.png', alt: 'Artisan' },
  { src: '/craftpic.png', alt: 'Craft Design + Build' },
  { src: '/h2hpic.png', alt: 'H2H' },
  { src: '/slatepic.png', alt: 'Slate' },
  { src: '/cohpic.png', alt: 'Coho' },
  { src: '/tablerpic.png', alt: 'Tabler' },
  { src: '/seepic.png', alt: 'See' },
  { src: '/panpic.png', alt: 'Pan' },
];

export const SUCCESS_TESTIMONIAL_VIDEOS = [
  {
    heading: 'SEE WHAT ARTISAN REMODELING HAD TO SAY',
    company: 'Artisan Remodeling',
    src: 'https://www.youtube.com/embed/3xaIQxT0qRg',
    title:
      'Onsite with Artisan Remodeling in Fort Collins, Colorado | Builders Breakthrough Advisors',
  },
  {
    heading: 'SEE WHAT CRAFT DESIGN + BUILD HAD TO SAY',
    company: 'Craft Design + Build',
    src: 'https://www.youtube.com/embed/2rqhILIPCGQ',
    title: 'BBA : Helping Business Scale to $10M +',
  },
  {
    heading: 'SEE WHAT CANN CONSTRUCTION HAD TO SAY',
    company: 'Cann Construction',
    src: 'https://www.youtube.com/embed/ELWzX7X3sNw',
    title: 'Another BBA Success : "More Than I Could Have Asked For..."',
  },
  {
    heading: 'SEE WHAT HHI BUILDERS HAD TO SAY',
    company: 'HHI Builders',
    src: 'https://www.youtube.com/embed/4vZNSch5ENc',
    title: "Don't Just Take Our Word For It - HHI Builders",
  },
];

const StartSuccessStories = ({ embedded = false }) => {
  const { darkMode } = useTheme();
  const [successStoriesIndex, setSuccessStoriesIndex] = useState(0);
  const sectionHeading = 'text-lg font-semibold text-gray-800 dark:text-white mb-4';
  const [selectedRevenueRange, setSelectedRevenueRange] = useState("0-1M");
  
  // Reset success stories index when revenue range changes
  useEffect(() => {
    setSuccessStoriesIndex(0);
  }, [selectedRevenueRange]);

  const mainContent = (
        <div className="space-y-8">
            <SuccessStoriesCarousel
              theme="start"
              successStoriesIndex={successStoriesIndex}
              setSuccessStoriesIndex={setSuccessStoriesIndex}
              selectedRevenueRange={selectedRevenueRange}
              setSelectedRevenueRange={setSelectedRevenueRange}
            />

            {/* Logo Loop */}
            <div className={`${startCard} flex items-center mb-8 overflow-hidden`} style={{ height: '160px', position: 'relative' }}>
              <LogoLoop
                logos={SUCCESS_PARTNER_LOGOS}
                speed={40}
                direction="left"
                logoHeight={100}
                gap={80}
                hoverSpeed={0}
                scaleOnHover
                fadeOut
                fadeOutColor={darkMode ? '#1f2937' : '#ffffff'}
                ariaLabel="Partner logos"
              />
            </div>

            {/* Partner Showcase Gallery */}
            <div className="mb-12">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {SUCCESS_PARTNER_GALLERY.map((pic) => (
                  <div
                    key={pic.src}
                    className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                  >
                    <img
                      src={pic.src}
                      alt={pic.alt}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* YouTube Videos Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {SUCCESS_TESTIMONIAL_VIDEOS.map((video) => (
                <div key={video.src} className={`${startCard} ${startCardPadding} text-center`}>
                  <h3 className={`${sectionHeading} text-sm`}>{video.heading}</h3>
                  <div className="flex justify-center">
                    <iframe
                      width="100%"
                      height="389"
                      src={video.src}
                      title={video.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                      className="rounded-lg w-full aspect-video"
                    ></iframe>
                  </div>
                </div>
              ))}
            </div>
        </div>
  );

  return (
    <StartSubpageLayout
      embedded={embedded}
      title="Success Stories"
      subtitle="Results by revenue band"
    >
      {mainContent}
    </StartSubpageLayout>
  );
};

export default StartSuccessStories;
